import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Navbar, useAuth } from "../components/Shared";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import { fetchVendorDetail, fetchPlans, getCheckoutQuote, createPaymentIntent, confirmPaymentManual } from "../lib/api";
import { getCheckoutDraft, clearCheckoutDraft } from "../lib/mockSub";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import AllergyWarning from "../components/AllergyWarning";

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { user, isPilot, configLoading } = useAuth();
  
  const [draft, setDraft] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [plans, setPlans] = useState([]);
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [allergyAcknowledged, setAllergyAcknowledged] = useState(false);

  // Check for success/cancel from Stripe redirect
  const orderId = searchParams.get("order_id");
  const canceled = searchParams.get("canceled");

  useEffect(() => {
    // Handle successful payment return from Stripe
    async function handlePaymentSuccess() {
      if (orderId && !canceled) {
        setProcessing(true);
        try {
          const result = await confirmPaymentManual(orderId);
          if (result.status === "PAID") {
            clearCheckoutDraft();
            toast({ title: "Payment successful!", description: "Your subscription is now active." });
            navigate("/dashboard");
          } else {
            setError("Payment is still processing. Please wait...");
          }
        } catch (err) {
          setError("Error confirming payment. Please check your dashboard.");
          setTimeout(() => navigate("/dashboard"), 3000);
        } finally {
          setProcessing(false);
        }
      }
    }
    handlePaymentSuccess();
  }, [orderId, canceled, navigate, toast]);

  useEffect(() => {
    const d = getCheckoutDraft();
    setDraft(d);
    if (d?.vendorId) {
      fetchVendorDetail(d.vendorId).then(setVendor).catch(() => setVendor(null));
    }
    fetchPlans().then(setPlans);
  }, []);

  useEffect(() => {
    async function runQuote() {
      if (!draft || !vendor?.company) return;
      
      // In pilot mode, force pickup
      const fulfillment = isPilot ? 'pickup' : draft.fulfillment;
      
      try {
        const q = await getCheckoutQuote({
          company_id: vendor.company.id,
          plan_id: draft.planId,
          meals: draft.meals,
          fulfillment: fulfillment,
          modifiers: draft.modifiers || []
        });
        setQuote(q);
        setQuoteError(null);
      } catch (e) {
        const detail = e?.response?.data?.detail || "Failed to get quote";
        setQuoteError(detail);
        setQuote(null);
      }
    }
    runQuote();
  }, [draft, vendor, isPilot]);

  const plan = useMemo(() => plans.find(p => p.id === draft?.planId), [plans, draft]);
  const perMeal = useMemo(() => vendor?.company?.plan_prices?.[draft?.planId] || 0, [vendor, draft]);
  const meals = draft?.meals || 0;
  const fulfillmentDisplay = isPilot ? 'Pickup' : (draft?.fulfillment === 'weekly' ? 'Delivery' : 'Pickup');

  async function handlePayment() {
    if (!quote || !user) return;
    
    setProcessing(true);
    setError(null);
    
    try {
      const fulfillmentMethod = isPilot ? 'pickup' : (draft.fulfillment === 'weekly' ? 'delivery' : 'pickup');
      
      const result = await createPaymentIntent({
        vendor_id: vendor.company.id,
        plan_id: draft.planId,
        meals: draft.meals,
        fulfillment_method: fulfillmentMethod,
        delivery_address: draft.address || null,
        selections: draft.selections.map(s => ({
          menu_item_id: s.menu_item_id,
          qty: s.qty
        })),
        modifiers: draft.modifiers || [],
        success_url: window.location.origin + "/checkout",
        cancel_url: window.location.origin + "/checkout"
      });
      
      // Redirect to Stripe Checkout
      if (result.checkout_url) {
        // Store order ID in sessionStorage for resume capability
        if (result.weekly_order_id) {
          sessionStorage.setItem('prepmate_pending_order', result.weekly_order_id);
        }
        window.location.href = result.checkout_url;
      } else {
        setError("Failed to create payment session");
        setProcessing(false);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Payment error";
      setError(detail);
      setProcessing(false);
      
      if (err?.response?.status === 409) {
        toast({ title: "Subscription conflict", description: detail });
        setTimeout(() => navigate("/dashboard"), 2000);
      } else if (err?.response?.status === 403) {
        toast({ title: "Not allowed", description: detail });
      }
    }
  }

  // Show processing state if returning from Stripe
  if (orderId && !canceled) {
    return (
      <div>
        <Navbar />
        <div className="mx-auto max-w-md px-4 py-20 text-center">
          <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-neutral-600" />
          <h1 className="text-xl font-semibold mb-2">Processing Payment</h1>
          <p className="text-sm text-neutral-600">Please wait while we confirm your payment...</p>
        </div>
      </div>
    );
  }

  // Show canceled state
  if (canceled) {
    return (
      <div>
        <Navbar />
        <div className="mx-auto max-w-md px-4 py-20 text-center">
          <XCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <h1 className="text-xl font-semibold mb-2">Payment Canceled</h1>
          <p className="text-sm text-neutral-600 mb-4">Your payment was canceled. You can try again.</p>
          <Button onClick={() => navigate(0)} className="bg-[#2a7d4f] text-white hover:bg-[#236840]">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (configLoading) {
    return (
      <div>
        <Navbar />
        <div className="mx-auto max-w-6xl px-4 py-6">
          <div className="animate-pulse grid gap-6 md:grid-cols-[1fr_380px]">
            <div className="space-y-4">
              {[1,2,3].map(i => <div key={i} className="rounded-lg border p-4 h-20 bg-neutral-200" />)}
            </div>
            <div className="rounded-lg border p-4 h-64 bg-neutral-200" />
          </div>
        </div>
      </div>
    );
  }

  if (!draft || !vendor) {
    // Check if user has a pending order they can resume
    const pendingOrderId = sessionStorage.getItem('prepmate_pending_order');
    
    return (
      <div>
        <Navbar />
        <div className="mx-auto max-w-6xl px-4 py-6">
          <Card>
            <CardContent className="p-6">
              <h1 className="text-xl font-semibold mb-2">No checkout in progress</h1>
              <p className="text-sm text-neutral-600 mb-4">
                {pendingOrderId 
                  ? "Your previous checkout session expired. Please start a new order."
                  : "Please select a meal plan first."
                }
              </p>
              <div className="flex gap-3">
                <Button onClick={() => navigate("/")} className="bg-[#2a7d4f] text-white hover:bg-[#236840]">
                  Browse Vendors
                </Button>
                {user?.vendor_id && (
                  <Button 
                    variant="outline" 
                    onClick={() => navigate(`/company/${user.vendor_id}`)}
                  >
                    Go to My Vendor
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div>
        <Navbar />
        <div className="mx-auto max-w-6xl px-4 py-6">
          <Card>
            <CardContent className="p-6">
              <h1 className="text-xl font-semibold mb-2">Sign in required</h1>
              <p className="text-sm text-neutral-600 mb-4">Please sign in to complete your checkout.</p>
              <Button onClick={() => navigate("/auth")} className="bg-[#2a7d4f] text-white hover:bg-[#236840]">
                Sign in
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 py-6 grid gap-6 md:grid-cols-[1fr_380px]">
        <div className="space-y-4">
          <Card>
            <CardContent className="p-4">
              <div className="font-semibold mb-2">Vendor</div>
              <div className="text-sm">{vendor.company.name}</div>
              <div className="text-xs text-neutral-600">
                {isPilot ? (
                  <span>Pickup only (pilot mode)</span>
                ) : (
                  <span>Delivery days: {(vendor.company.delivery_days || []).join(', ') || '—'} • Pickup: {(vendor.company.pickup_days || []).join(', ') || '—'}</span>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="font-semibold mb-2">Weekly selections</div>
              <div className="space-y-2">
                {draft.selections.map((s, idx) => {
                  const item = (vendor.menu || []).find(it => it.id === s.menu_item_id);
                  return (
                    <div key={idx} className="text-sm flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{item?.name || s.menu_item_id}</div>
                        <div className="text-xs text-neutral-600">Qty: {s.qty}</div>
                      </div>
                      <div className="text-xs text-neutral-600">
                        {item?.calories ? `${item.calories} kcal • ` : ''}
                        {item?.macros ? `${item.macros.carbs_g || 0}C/${item.macros.protein_g || 0}P/${item.macros.fat_g || 0}F g` : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
              {draft.modifiers?.length > 0 && (
                <div className="mt-2 text-xs text-neutral-600">Modifiers: {draft.modifiers.join(', ')}</div>
              )}
            </CardContent>
          </Card>

          {/* Allergy notice + acknowledgment */}
          <Card>
            <CardContent className="p-4">
              <AllergyWarning />
              <label className="flex items-start gap-2 text-xs text-neutral-600 cursor-pointer mt-3">
                <input
                  type="checkbox"
                  checked={allergyAcknowledged}
                  onChange={e => setAllergyAcknowledged(e.target.checked)}
                  className="mt-0.5 shrink-0"
                />
                <span>I acknowledge that food is prepared by independent vendors and that I am responsible for reviewing ingredient and allergen information before ordering.</span>
              </label>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="font-semibold mb-2">Fulfillment</div>
              <div className="text-sm">{fulfillmentDisplay}</div>
              {!isPilot && draft.fulfillment === 'weekly' && (
                <div className="text-xs text-neutral-600">Address: {draft.address}</div>
              )}
              {isPilot && (
                <div className="text-xs text-amber-600 mt-1">During pilot, only pickup is available</div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="rounded-2xl border bg-white p-4 h-max sticky top-20">
          <div className="font-semibold mb-3">Price breakdown</div>
          
          {quoteError ? (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2 mb-3">
              {quoteError}
            </div>
          ) : quote ? (
            <div className="text-sm space-y-1 mb-4">
              <div className="flex justify-between">
                <span>Base ({meals} meals @ ${perMeal.toFixed(2)}/meal)</span>
                <span>${quote.base.toFixed(2)}</span>
              </div>
              {quote.modifiers_total > 0 && (
                <div className="flex justify-between">
                  <span>Modifiers</span>
                  <span>${quote.modifiers_total.toFixed(2)}</span>
                </div>
              )}
              {quote.delivery_fee > 0 && (
                <div className="flex justify-between">
                  <span>Delivery fee</span>
                  <span>${quote.delivery_fee.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span>Tax ({(quote.tax_rate * 100).toFixed(2)}%)</span>
                <span>${quote.tax_amount.toFixed(2)}</span>
              </div>
              {quote.platform_fee_amount > 0 && (
                <div className="flex justify-between text-neutral-500">
                  <span>Platform fee (vendor)</span>
                  <span>${quote.platform_fee_amount.toFixed(2)}</span>
                </div>
              )}
              <div className="border-t pt-2 flex justify-between font-semibold">
                <span>Total weekly</span>
                <span>${quote.total.toFixed(2)}</span>
              </div>
            </div>
          ) : (
            <div className="text-sm text-neutral-600 mb-4">Calculating price...</div>
          )}

          {error && (
            <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
              {error}
            </div>
          )}

          {quote && (
            <>
              <p className="text-xs text-neutral-500 mb-3 leading-relaxed">
                By completing this purchase you agree to the PrepMate{" "}
                <a href="/terms" className="underline hover:text-neutral-800" target="_blank" rel="noopener noreferrer">Terms of Service</a>.
                Food products are prepared and sold by an independent vendor. PrepMate is a technology marketplace and is not responsible for food quality, safety, or allergen content.{" "}
                <a href="/disclaimer" className="underline hover:text-neutral-800" target="_blank" rel="noopener noreferrer">Full disclaimer</a>.
              </p>
              {!allergyAcknowledged && !processing && (
                <p className="text-xs text-amber-600 mb-2">
                  Please acknowledge the allergy notice above to continue.
                </p>
              )}
              <Button
                onClick={handlePayment}
                disabled={processing || !quote || !allergyAcknowledged}
                className="w-full bg-[#2a7d4f] text-white hover:bg-[#236840]"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  `Pay $${quote.total.toFixed(2)} with Stripe`
                )}
              </Button>
            </>
          )}

          <p className="text-xs text-neutral-500 mt-2 text-center">
            Secure payment powered by Stripe
          </p>
        </div>
      </div>
    </div>
  );
}

// Checkout Success Page Component (separate route)
export function CheckoutSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const orderId = searchParams.get("order_id");
  const [status, setStatus] = useState("processing");

  useEffect(() => {
    async function confirmPayment() {
      if (!orderId) {
        setStatus("error");
        return;
      }
      
      try {
        const result = await confirmPaymentManual(orderId);
        if (result.status === "PAID") {
          clearCheckoutDraft();
          setStatus("success");
          toast({ title: "Payment successful!", description: "Your subscription is now active." });
          setTimeout(() => navigate("/dashboard"), 2000);
        } else {
          setStatus("error");
        }
      } catch (err) {
        setStatus("error");
        setTimeout(() => navigate("/dashboard"), 3000);
      }
    }
    
    confirmPayment();
  }, [orderId, navigate, toast]);

  return (
    <div>
      <Navbar />
      <div className="mx-auto max-w-md px-4 py-20 text-center">
        {status === "processing" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-neutral-600" />
            <h1 className="text-xl font-semibold mb-2">Processing Payment</h1>
            <p className="text-sm text-neutral-600">Please wait while we confirm your payment...</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
            <h1 className="text-xl font-semibold mb-2">Payment Successful!</h1>
            <p className="text-sm text-neutral-600">Redirecting to your dashboard...</p>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
            <h1 className="text-xl font-semibold mb-2">Payment Error</h1>
            <p className="text-sm text-neutral-600 mb-4">There was an issue confirming your payment. Please check your dashboard.</p>
            <Button onClick={() => navigate("/dashboard")} className="bg-[#2a7d4f] text-white hover:bg-[#236840]">
              Go to Dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
